import { FechaCR, MontoCRC } from '@/components/dominio';
import { ListasContadoTabla } from '@/components/solicitudes-contado/ListasContadoTabla';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useListasContado } from '@/hooks/useListasContado';
import { useEliminarPropuestaPago, usePropuestasPago } from '@/hooks/usePropuestasPago';
import { useUsuarioActual } from '@/hooks/useSolicitudesContado';
import { type TipoPropuestaPago, formatearUSD } from '@adelante/shared';
import { CircleNotch, FileArrowDown, Plus, Trash } from '@phosphor-icons/react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

export function PropuestasPagoPantalla() {
  const { data, isLoading } = usePropuestasPago();
  const eliminar = useEliminarPropuestaPago();
  const { data: yoData } = useUsuarioActual();
  const esAdmin = !!yoData?.data?.esAdmin;
  const { data: listasData } = useListasContado();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tipo: TipoPropuestaPago = params.get('tipo') === 'contado' ? 'CONTADO' : 'CREDITO';
  const parametroTipo = tipo === 'CONTADO' ? 'contado' : 'credito';

  const todas = data?.data ?? [];
  const propuestas = todas.filter((p) => p.tipo === tipo);
  const cantidadPorTipo = {
    CREDITO: todas.filter((p) => p.tipo === 'CREDITO').length,
    // En contado se muestran las listas de pagos de contado, no propuestas de facturas.
    CONTADO: (listasData?.data ?? []).length,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Pago Proveedores</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {tipo === 'CONTADO'
              ? 'Listas de pagos de contado: lo aprobado en Solicitudes queda en una lista con nombre y fecha, de la que sale el archivo de banco.'
              : 'Vista previa de qué facturas se pagarían — todavía no escribe nada en Business Central.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {tipo === 'CONTADO' ? (
            <Button asChild>
              <Link to="/vencimientos/solicitudes">Ir a las solicitudes de contado</Link>
            </Button>
          ) : esAdmin ? (
            <Button asChild>
              <Link to={`/vencimientos/propuestas/nueva?tipo=${parametroTipo}`}>
                <Plus className="h-4 w-4" weight="bold" aria-hidden="true" />
                Nueva propuesta de crédito
              </Link>
            </Button>
          ) : null}
        </div>
      </div>

      <fieldset
        aria-label="Tipo de propuesta"
        className="m-0 inline-flex overflow-hidden rounded-md border border-border p-0"
      >
        {(['CREDITO', 'CONTADO'] as const).map((t) => (
          <Button
            key={t}
            type="button"
            variant={tipo === t ? 'default' : 'ghost'}
            aria-pressed={tipo === t}
            className="rounded-none"
            onClick={() => setParams(t === 'CONTADO' ? { tipo: 'contado' } : {})}
          >
            {t === 'CONTADO' ? 'Contado' : 'Crédito'}
            <span className="ml-2 text-xs opacity-70">{cantidadPorTipo[t]}</span>
          </Button>
        ))}
      </fieldset>

      {tipo === 'CONTADO' ? (
        <ListasContadoTabla />
      ) : isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
          Cargando…
        </div>
      ) : propuestas.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            No hay propuestas de crédito todavía. Elegí un criterio (términos de pago + fecha de
            corte) con "Nueva propuesta" para generarla.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Fecha de pago</TableHead>
                  <TableHead className="text-right">Facturas</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {propuestas.map((p) => (
                  <TableRow
                    key={p.idPropuesta}
                    className="cursor-pointer"
                    onClick={() => navigate(`/vencimientos/propuestas/${p.idPropuesta}`)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {p.nombre}
                        {p.cantidadArchivosBanco > 0 && (
                          <Badge
                            variant="success"
                            className="gap-1 text-xs font-normal"
                            title={`${p.cantidadArchivosBanco} archivo(s) de banco ya generados — abrí la propuesta para descargarlos`}
                          >
                            <FileArrowDown className="h-3 w-3" weight="bold" aria-hidden="true" />
                            Archivo generado
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {p.fechaPagoPropuesta ? <FechaCR fecha={p.fechaPagoPropuesta} /> : '—'}
                    </TableCell>
                    <TableCell className="text-right">{p.cantidadFacturas}</TableCell>
                    <TableCell className="text-right">
                      {p.totalPorMoneda.map((t) => (
                        <div key={t.currencyCode}>
                          {t.currencyCode === 'USD' ? (
                            formatearUSD(t.total)
                          ) : (
                            <MontoCRC monto={t.total} />
                          )}
                        </div>
                      ))}
                    </TableCell>
                    <TableCell>
                      {esAdmin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!confirm(`¿Eliminar la propuesta "${p.nombre}"?`)) return;
                            eliminar.mutate(p.idPropuesta, {
                              onSuccess: () => toast.success('Propuesta eliminada'),
                              onError: (e) =>
                                toast.error(e instanceof Error ? e.message : 'Error al eliminar'),
                            });
                          }}
                        >
                          <Trash className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
